import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import PrimaryButton from "../../../../common/ui/PrimaryButton";
import SecondaryButton from "../../../../common/ui/SecondaryButton";
import BioAgeClockCard from "../shared/BioAgeClockCard";
import BioAgeAnalysisCard from "./BioAgeAnalysisCard";
import { useConsultation } from "../../ConsultationContext";

/**
 * Splash — the consultation's landing page, OUTSIDE the stepper flow.
 * Visual reference: Figma node 14711:145898.
 *
 * Renders the bio-age clock plus the AI Guided / Self Guided choice. Either
 * button enters the stepper at Section 1 · Step 1.
 */
const SplashPanel: React.FC = () => {
  const theme: Theme = useTheme();
  const { stepper, enterAiGuided, demoMode } = useConsultation();

  // AI Guided: writes `?ai=1` + stepper-entry params in one URL update, so
  // the ElevenLabs agent mounts on this load AND on any subsequent reload.
  // Self Guided: just enters the stepper, no AI flag.
  // In demo mode the AI agent is unavailable, so "AI Guided" is shown but
  // disabled — the option stays visible, only Self Guided works.
  return (
    <BioAgeAnalysisCard>
      <Box sx={styles.content}>
        <BioAgeClockCard size={160} showSubtitle={false} />
        <Box sx={styles.prompt(theme)}>How would you like to explore?</Box>
        <Box sx={styles.disclaimer(theme)}>
          <Box component="span" sx={{ fontFamily: "var(--font-Bold)" }}>
            Disclaimer:
          </Box>{" "}
          The present Assessment/Consultation is intended for information
          purposes only and under no circumstances should it be considered a
          substitute for professional medical advice, diagnosis or treatment.
          You need to consult your physician and/or family doctor prior to
          engaging in any exercise program and/or changing your diet and/or
          habits as a result of the information provided by the present
          Assessment/Consultation. Company makes no representation that the
          present Assessment/Consultation will result in any improvement of your
          health and fitness status. You agree that participating in any workout
          regimen, physical exercise or activity may result in an increased risk
          of physical injury based on the nature, frequency, intensity and
          duration of the workout regimen, physical exercise or activity. You
          agree that if you participate in any workout regimen, physical
          exercise or activity, you do so at your own risk and you assume the
          risk of any and all injury and/or damage you may suffer.
        </Box>
        <Box sx={styles.choiceRow}>
          <PrimaryButton
            label="AI Guided"
            onClick={enterAiGuided}
            isDisable={demoMode}
            sx={styles.choiceBtn}
          />
          <SecondaryButton
            label="Self Guided"
            onClick={stepper.enterStepper}
            sx={styles.choiceBtn}
          />
        </Box>
      </Box>
    </BioAgeAnalysisCard>
  );
};

export default SplashPanel;

const styles = {
  content: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "24px",
    // The card centres its content as a group, which pulls the clock higher
    // than the stepper's clock (the stepper has less content below it). Nudge
    // the group down so the clock lands at the same vertical position as
    // Step 1's clock — keeping it put when entering the stepper.
    transform: "translateY(17px)",
  },
  prompt: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as const,
  }),
  choiceRow: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
  },
  choiceBtn: {
    height: "auto",
    padding: "8px 16px",
    fontSize: "12px",
    "& > div": { flex: "0 0 auto" },
  },
  disclaimer: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: 1.5,
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    textAlign: "center" as const,
    maxWidth: "710px",
  }),
};
